"use client";

import { useState } from "react";
import { Button } from "@/components/button";

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

export function ContactForm() {
  const [state, setState] = useState<State>({ kind: "idle" });

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState({ kind: "submitting" });
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      company: String(fd.get("company") ?? "").trim(),
      budget: String(fd.get("budget") ?? "").trim(),
      message: String(fd.get("message") ?? "").trim(),
      // honeypot
      website: String(fd.get("website") ?? "").trim(),
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? `Request failed: ${res.status}`);
      }
      setState({ kind: "ok" });
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Something went wrong",
      });
    }
  }

  if (state.kind === "ok") {
    return (
      <div className="rounded border border-brand-green/40 bg-brand-green/5 p-6">
        <p className="text-[12px] text-brand-green font-semibold tracking-widest">
          // 200 OK
        </p>
        <h2 className="mt-2 text-[22px] font-semibold">Message received.</h2>
        <p className="mt-2 text-[14px] text-ink-secondary leading-[1.65]">
          We&apos;ll reply within one business day. If it&apos;s urgent, email{" "}
          <a className="text-brand-green hover:underline" href="mailto:hello@strikeshow.com">
            hello@strikeshow.com
          </a>
          .
        </p>
      </div>
    );
  }

  const inputClass =
    "w-full rounded border border-surface-border focus:border-brand-green focus:ring-2 focus:ring-brand-green/15 bg-surface-card px-3.5 py-2.5 text-[14px] outline-none transition-colors";

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="name" name="name" required>
          <input name="name" required className={inputClass} placeholder="$ Jane Doe" />
        </Field>
        <Field label="email" name="email" required>
          <input
            name="email"
            type="email"
            required
            className={inputClass}
            placeholder="$ jane@company.com"
          />
        </Field>
        <Field label="company" name="company">
          <input name="company" className={inputClass} placeholder="$ Acme Co." />
        </Field>
        <Field label="budget (rough)" name="budget">
          <select name="budget" className={inputClass} defaultValue="">
            <option value="">$ select…</option>
            <option value="under-10k">under $10k</option>
            <option value="10-25k">$10k – $25k</option>
            <option value="25-75k">$25k – $75k</option>
            <option value="75k+">$75k+</option>
            <option value="retainer">monthly retainer</option>
            <option value="not-sure">not sure yet</option>
          </select>
        </Field>
      </div>

      <Field label="what do you want built?" name="message" required>
        <textarea
          name="message"
          required
          rows={6}
          className={inputClass}
          placeholder="$ describe the problem, the workflow, what you've tried, deadlines, anything relevant…"
        />
      </Field>

      {/* Honeypot field — hidden from real users */}
      <div aria-hidden className="hidden">
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {state.kind === "error" && (
        <p className="text-[13px] text-error">
          <span className="font-semibold">stderr:</span> {state.message}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={state.kind === "submitting"}>
          {state.kind === "submitting" ? "sending…" : "Send message"}
        </Button>
        <span className="text-[12px] text-ink-muted">
          // we never share your info, ever
        </span>
      </div>
    </form>
  );
}

function Field({
  label,
  name,
  required,
  children,
}: {
  label: string;
  name: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[12px] text-ink-muted block mb-1.5">
        <span className="text-brand-green">//</span> {label}
        {required && <span className="text-error"> *</span>}
        <span className="text-ink-muted/60"> ({name})</span>
      </span>
      {children}
    </label>
  );
}
